import {
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { S3_URL_BASE } from 'src/environments/environment';

@Component({
  selector: 'app-input-file',
  templateUrl: './input-file.component.html',
  styleUrls: ['./input-file.component.sass'],
})
export class InputFileComponent implements OnInit, OnDestroy {
  @Input()
  config: IFieldConfig;
  @Input()
  group: FormGroup;

  @Input()
  mask: string;

  @Input()
  typeCes?: TypeCesEnum | undefined;

  formSub: Subscription;

  constructor() { }

  ngOnInit(): void {
    this.formSub = this.group.valueChanges.subscribe((value) => {
      if (!(value[this.config.name] instanceof File)) {
        const fileValue = value[this.config.name];

        if (fileValue && typeof fileValue === 'object') {
          if (fileValue.originalname) {
            this.fileName = fileValue.originalname;
          } else if (fileValue.name) {
            this.fileName = fileValue.name;
          } else {
            const templateName = this.group.get('contractTemplateName')?.value;
            if (templateName) {
              this.fileName = templateName;
            } else {
              const fileName = this.group.get('fileName')?.value;
              if (fileName) {
                this.fileName = fileName;
              } else {
                const completeValue = this.group.getRawValue();
                if (completeValue.contractTemplateName) {
                  this.fileName = completeValue.contractTemplateName;
                } else if (completeValue.fileName) {
                  this.fileName = completeValue.fileName;
                }
              }
            }
          }
        } else {
          this.fileName = fileValue;
        }
      }
    });

    setTimeout(() => {
      const value = this.group.getRawValue();

      if (value.contractTemplateName) {
        this.fileName = value.contractTemplateName;
      } else if (value.fileName) {
        this.fileName = value.fileName;
      } else {
        const fieldValue = value[this.config.name];

        if (fieldValue && typeof fieldValue === 'string') {
          this.fileName = this.extractFileNameFromUrl(fieldValue);
        }
      }
    }, 100);
  }

  private extractFileNameFromUrl(url: string): string {
    if (!url) return '';

    // Remove parâmetros de query string
    const cleanUrl = url.split('?')[0];

    // Remove o caminho e pega apenas o nome do arquivo
    const parts = cleanUrl.split('/');
    const fileName = parts[parts.length - 1];

    if (!fileName || fileName === 'SERVICE-ORDERS') {
      const serviceOrdersIndex = cleanUrl.indexOf('SERVICE-ORDERS/');
      if (serviceOrdersIndex !== -1) {
        const afterServiceOrders = cleanUrl.substring(serviceOrdersIndex + 'SERVICE-ORDERS/'.length);
        if (afterServiceOrders) {
          return afterServiceOrders;
        }
      }
    }

    return fileName || 'arquivo';
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
  }

  get typeCesEnum() {
    return TypeCesEnum;
  }

  get messagesEnum() {
    return MessagesEnum;
  }

  @Input()
  requiredFileType: string;

  fileName = '';
  uploadProgress: number;
  uploadSub: Subscription;

  onFileSelected(event: any) {
    const file: File = event.target.files[0];

    if (file) {
      this.fileName = file.name;
      this.group.get(this.config.name)?.setValue(file);
    }
  }

  getFileUri() {
    const templateUrl = this.group.get('contractTemplateUrl')?.value;
    if (templateUrl) {
      if (templateUrl.startsWith('http')) {
        return templateUrl;
      }
      return `${S3_URL_BASE}${templateUrl}`;
    }

    const value = this.group.get(this.config.name)?.getRawValue();
    if (typeof value === 'string' && value) {
      if (value.includes('CONTRACT_TEMPLATES/')) {
        return `${S3_URL_BASE}${value}`;
      }
    }

    return '';
  }

  public cancelFile() {
    this.fileName = '';
    this.group.get(this.config.name)?.setValue('');
  }

  public downloadFile() {
    const url = this.getCompleteFileUrl();

    if (url) {
      return url;
    }

    console.warn('⚠️ Não foi possível determinar a URL correta para download');
    return '';
  }

  public downloadFileDirectly(event: Event) {
    event.preventDefault();

    const url = this.getCompleteFileUrl();

    if (!url) {
      console.error('❌ URL não encontrada para download');
      return;
    }

    const fileExtension = this.getFileExtension(this.fileName || url);

    // Lista de extensões que devem ser baixadas automaticamente (não abrir em nova aba)
    const downloadExtensions = [
      'html', 'htm', 'doc', 'docx', // Documentos
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', // Imagens
      'mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', // Vídeos
      'mp3', 'wav', 'ogg', 'aac', 'flac', // Áudios
      'pdf', 'txt', 'csv', 'xlsx', 'xls', 'ppt', 'pptx' // Outros documentos
    ];

    if (downloadExtensions.includes(fileExtension)) {
      console.log(`📄 Arquivo do tipo ${fileExtension} detectado, usando XMLHttpRequest para download`);

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const downloadUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = this.fileName || 'download';
          document.body.appendChild(link);
          link.click();

          // Limpar recursos
          document.body.removeChild(link);
          setTimeout(() => {
            URL.revokeObjectURL(downloadUrl);
          }, 100);
        } else {
          console.error('❌ Erro ao baixar o arquivo:', xhr.statusText);
        }
      };

      xhr.onerror = () => {
        console.error('❌ Erro de rede ao tentar baixar o arquivo');
      };

      xhr.send();
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = this.fileName || 'download';
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getFileExtension(filename: string): string {
    if (!filename) return '';
    const cleanName = filename.split('?')[0];

    const parts = cleanName.split('.');
    if (parts.length > 1) {
      return parts[parts.length - 1].toLowerCase();
    }

    return '';
  }

  private getCompleteFileUrl(): string {

    const templateUrl = this.group.get('contractTemplateUrl')?.value;

    if (templateUrl) {
      if (templateUrl.startsWith('http')) {
        return templateUrl;
      }

      if (!templateUrl.includes('CONTRACT_TEMPLATES/')) {
        const correctedUrl = `CONTRACT_TEMPLATES/${templateUrl}`;
        return `${S3_URL_BASE}${correctedUrl}`;
      }

      return `${S3_URL_BASE}${templateUrl}`;
    }

    const fileUrl = this.group.get('fileUrl')?.value;

    if (fileUrl) {
      if (fileUrl.startsWith('http')) {
        return fileUrl;
      }
      return `${S3_URL_BASE}${fileUrl}`;
    }

    const fieldValue = this.group.get(this.config.name)?.value;

    if (fieldValue && typeof fieldValue === 'string') {
      if (fieldValue.startsWith('http')) {
        return fieldValue;
      }

      // Se é um caminho relativo, adicionar a base do S3
      if (fieldValue.includes('SERVICE-ORDERS/') || fieldValue.includes('TREINAMENTOS/') || fieldValue.includes('CONTRACT_TEMPLATES/')) {
        const url = `${S3_URL_BASE}${fieldValue}`;
        return url;
      } else {

        if (this.config.name === 'fileAttachment' && this.group.get('trainingName')?.value) {
          const url = `${S3_URL_BASE}TREINAMENTOS/${fieldValue}`;
          return url;
        }

        const url = `${S3_URL_BASE}SERVICE-ORDERS/${fieldValue}`;
        return url;
      }
    }

    // Tentar o valor do próprio campo (fallback)
    const value = this.group.get(this.config.name)?.getRawValue();

    if (typeof value === 'string' && value) {
      if (value.includes('CONTRACT_TEMPLATES/')) {
        const url = `${S3_URL_BASE}${value}`;
        return url;
      } else if (value.startsWith('http')) {
        return value;
      } else if (value.includes('SERVICE-ORDERS/')) {
        const url = `${S3_URL_BASE}${value}`;
        return url;
      } else if (value) {
        const url = `${S3_URL_BASE}SERVICE-ORDERS/${value}`;
        return url;
      }
    }

    return '';
  }
}